import { useTargetingContext } from '../../../utils/context/TargetingContext';
import { ContextualSegmentsItems } from '../ContextualSegments/ContextualSegmentsItems';
import { ContextualSegmentsTopBox } from '../ContextualSegments/ContextualSegmentsTopBox';
import { ContextualSegmentsItemsV2 } from '../ContextualSegmentsV2/ContextualSegmentsItemsV2';
import { ContextualSegmentsTopBoxV2 } from '../ContextualSegmentsV2/ContextualSegmentsTopBoxV2';
import { useAccountContextualProvider } from './useGetContextualData';

/** V2 edits the provider connected to the account. */
const useV2AccountProviderId = (accountUid: string): string | undefined =>
  useAccountContextualProvider(accountUid).providerId;

/** V1 has no account provider: providers are typed in, and DSP / publisher UI do not fetch related accounts. */
const useNoAccountProviderId = (_accountUid: string): string | undefined => undefined;

/**
 * Contextual Segments Component Selector Hook
 *
 * This hook determines which contextual segments implementation to use based on user context.
 *
 * THREE IMPLEMENTATIONS EXIST:
 * 1. V1 (DSP UI) - Manual provider entry, basic text input
 *    Location: AdvancedTargeting/ContextualSegments/
 *    Users: isOa === false (DSP UI, legacy systems)
 *
 * 2. V2 (OA UI Current) - Provider auto-detection, dropdown selection, bulk add
 *    Location: AdvancedTargeting/ContextualSegmentsV2/
 *    Users: isOa === true (OA UI, before GrowthBook flag rollout)
 *
 * 3. V3/Standalone (OA UI Future) - SelectionDrawer pattern, multi-provider support
 *    Location: Targeting/ContextualSegments/
 *    Users: isOa === true + GrowthBook flag ON (after feature flag rollout)
 *    Status: Dormant (commented out in useTargeting.tsx)
 *
 * SELECTION LOGIC:
 * - isOa === false → V1 (DSP UI)
 * - isOa === true → V2 (OA UI, until flag activates V3)
 * - Future: isOa === true + flag === true → V3 (handled in useTargeting.tsx, not here)
 *
 * See docs/contextual-segments-three-implementations.md for full details.
 */
export const useGetContextualSegmentsComponents = () => {
  const { isOa } = useTargetingContext();

  if (isOa) {
    // OA UI → V2 Implementation (dropdown-based, bulk add)
    return {
      ContextualSegmentsItems: ContextualSegmentsItemsV2,
      ContextualSegmentsTopBox: ContextualSegmentsTopBoxV2,
      segmentsV2: true,
      useAccountProviderId: useV2AccountProviderId,
    };
  }

  // DSP UI → V1 Implementation (manual entry)
  return {
    ContextualSegmentsItems: ContextualSegmentsItems,
    ContextualSegmentsTopBox: ContextualSegmentsTopBox,
    segmentsV2: false,
    useAccountProviderId: useNoAccountProviderId,
  };
};
